// File: frontend/src/components/TenantBoundary.js
//
// The render gate for tenant business pages.
//
// WHY THIS EXISTS
// ---------------
// Login is an SPA navigation, so the providers above <Router> never remount and
// the page components mount immediately — before the new account's identity and
// permissions have been resolved. That is the window in which the dashboard
// painted the PREVIOUS account's cached payload.
//
// This component makes the transition explicit. Tenant pages render only when
// the app is `ready` for the CURRENT account; during any transition the customer
// sees a neutral screen that contains no tenant information whatsoever.
//
// It is a gate, not the security boundary. The account-scoped cache key
// (utils/accountScope + Dashboard) and the backend's tenant predicates remain
// the actual isolation; this stops the leak from ever being reachable.

import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { openWorkspace } from "../services/handoff";
import { usePermissions } from "../context/PermissionsContext";
import { getAccountIdentity, onAuthEpochChange } from "../utils/accountScope";
import { authService } from "../services/api";

/** anonymous | authenticating | no_tenant | hydrating_tenant | ready */
export function useTenantReadiness() {
  const { loading, loadedOnce } = usePermissions();
  const [, forceTick] = useState(0);

  // Re-evaluate on every account change so the status cannot go stale.
  useEffect(() => onAuthEpochChange(() => forceTick((n) => n + 1)), []);
  useEffect(() => {
    const onAuth = () => forceTick((n) => n + 1);
    window.addEventListener("pesafii:auth-changed", onAuth);
    return () => window.removeEventListener("pesafii:auth-changed", onAuth);
  }, []);

  const user = authService.getCurrentUser();
  const { tenantId, userId } = getAccountIdentity();

  if (!user) return { status: "anonymous", tenantId: null, userId: null };

  // ── "NO TENANT" IS AN ANSWER, NOT A WAITING ROOM ──────────────────────────
  //
  // This used to read `tenantId == null → still authenticating`, which was true
  // while the only way to hold an account was to own a business. A PesaFii
  // Ambassador may genuinely never have one, and for them that branch never
  // resolved: they landed on "Loading your workspace…" and stayed there
  // forever, because there was nothing still to load.
  //
  // The two cases are told apart by the USER id. A signed-in person always has
  // one; a missing userId means the stored account really is still being read.
  // A missing tenantId alongside a present userId is a settled fact.
  if (userId == null) return { status: "authenticating", tenantId, userId };
  if (tenantId == null) return { status: "no_tenant", tenantId, userId };
  // Permissions must have resolved FOR THIS account. PermissionsContext resets
  // to empty + loading on an identity change, so `loading` here genuinely means
  // "this account's grants are not known yet".
  if (loading || !loadedOnce) return { status: "hydrating_tenant", tenantId, userId };
  return { status: "ready", tenantId, userId };
}

function NeutralLoader({ label }) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        minHeight: "60vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 1.5,
      }}
    >
      <CircularProgress size={28} />
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{label}</Typography>
    </Box>
  );
}

/**
 * A signed-in person with no business, on the BUSINESS application. There is
 * nothing to show them here, and no amount of waiting changes that, so they
 * choose: create a business on this same login, or go to their Ambassador
 * workspace. It used to redirect to /ambassador, which is now the public
 * programme page, and the Ambassador workspace is a different application.
 */
function NoBusinessYet() {
  const navigate = useNavigate();
  return (
    <Box sx={{ minHeight: "70vh", display: "grid", placeItems: "center", px: 2 }}>
      <Box sx={{ maxWidth: 460, textAlign: "center" }}>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          No business on this account yet
        </Typography>
        <Typography sx={{ color: "text.secondary", mb: 3 }}>
          This PesaFii account doesn't run a hotspot business. You can create one with the same login, or open your PesaFii Ambassadors workspace.
        </Typography>
        <Stack spacing={1.25}>
          <Button variant="contained" size="large" onClick={() => navigate("/business/create")}>Create a business</Button>
          <Button variant="outlined" size="large" onClick={() => openWorkspace("ambassador", "/")}>Open PesaFii Ambassadors</Button>
          <Button size="small" onClick={() => authService.logoutAndRedirect("/login")}>Sign out</Button>
        </Stack>
      </Box>
    </Box>
  );
}

/**
 * Renders `children` only when the app is ready for the current account.
 *
 * `children` is keyed by `tenantId:userId`, so every page component below is
 * structurally new on an account change — no page-local React state can survive
 * from the previous account by construction.
 */
export default function TenantBoundary({ children }) {
  const { status, tenantId, userId } = useTenantReadiness();

  // A person with no business has nothing to show on a business page, and no
  // amount of waiting will change that. They choose (see NoBusinessYet).
  if (status === "no_tenant") {
    return <NoBusinessYet />;
  }

  // Anonymous is handled by PrivateRoute (redirect to /login); rendering a
  // neutral screen here avoids a flash of tenant chrome during the redirect.
  if (status !== "ready") {
    return <NeutralLoader label={status === "anonymous" ? "Signing out…" : "Loading your workspace…"} />;
  }
  return <React.Fragment key={`${tenantId}:${userId}`}>{children}</React.Fragment>;
}
